'use client';

export const dynamic = 'force-dynamic';

import React, {
	useCallback,
	useEffect,
	useRef,
	useState,
} from 'react';
import api from '@/utils/api';
import { useAuth } from '@/contexts/AuthContext';
import { BUSINESS_TYPES } from '@/constants/businessTypes';
import Link from 'next/link';
import {
	Search,
	Filter,
	ChevronLeft,
	ChevronRight,
	Store as StoreIcon,
	MapPin,
	ExternalLink,
	X,
	Loader2,
	CheckCircle2,
} from 'lucide-react';

// --- Types ---

type Store = {
	_id: string;
	name: string;
	slug: string;
	description?: string;
	university?: string;
	logoUrl?: string | null;
	metadata?: {
		category?: string;
		businessType?: string;
	};
	createdAt?: string;
	status?: string;
	owner?: {
		_id?: string;
		isAlumni?: boolean;
	};
};

const DEFAULT_LIMIT = 12;

function safeNumber(
	v: string | null | undefined,
	fallback = 0,
) {
	if (!v) return fallback;
	const n = Number(v);
	return Number.isFinite(n) ? n : fallback;
}

// --- Main Component ---

export default function StoresPage() {
	const { user } = useAuth();
	// UI State
	const [q, setQ] = useState('');
	const [category, setCategory] = useState<string | null>(
		null,
	);
	const [sort, setSort] = useState('newest');
	const [page, setPage] = useState(1);

	// Data State
	const [stores, setStores] = useState<Store[]>([]);
	// const [categories, setCategories] = useState<string[]>([]); // Removed dynamic categories
	const [total, setTotal] = useState(0);
	const [loading, setLoading] = useState(true);
	const [showMobileFilters, setShowMobileFilters] =
		useState(false);

	// Refs
	const searchRef = useRef<number | null>(null);

	// --- Helpers ---

	const buildParams = useCallback(() => {
		const params: any = {
			limit: DEFAULT_LIMIT,
			page,
			sort,
		};
		if (q) params.q = q;
		if (category) params.businessType = category; // Map selected category to businessType param
		return params;
	}, [q, category, sort, page]);

	// --- Data Fetching ---

	const fetchStores = useCallback(async () => {
		setLoading(true);
		try {
			const params = buildParams();
			// Ensure we filter for approved stores via API
			// params.status = 'approved';

			const res = await api.get('/stores', { params });
			const data = res.data?.data ?? res.data?.stores ?? [];
			const meta = res.data?.meta ?? { total: data.length };

			// Client side filter fallback just in case
			let approved = Array.isArray(data)
				? data.filter(
						(s: any) =>
							(s.status ?? 'approved') === 'approved',
				  )
				: [];

			// Filter out own stores
			if (user?.id) {
				approved = approved.filter(s => String(s.owner?._id || s.owner) !== String(user.id));
			}

			setStores(approved);
			setTotal(Number(meta.total ?? approved.length));

			// Derive categories if not explicitly loaded yet
			/*
			if (categories.length === 0 && approved.length > 0) {
				const derived = Array.from(
					new Set(
						approved
							.map((s: Store) => s.metadata?.category)
							.filter(Boolean),
					),
				).sort() as string[];
				setCategories(derived);
			} 
			*/
		} catch (err) {
			console.error('Failed to load stores', err);
			setStores([]);
		} finally {
			setLoading(false);
		}
	}, [buildParams]); // Removed categories.length dependency

	// Initial Load & Category Fetch
	useEffect(() => {
		// Optional: Fetch explicit store categories endpoint if you have it
		// api.get('/stores/categories').then(res => setCategories(res.data));
		fetchStores();
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [page, sort, category]); // Trigger fetch when these change

	// Search Debounce
	const handleSearchChange = (val: string) => {
		setQ(val);
		setPage(1);
		if (searchRef.current) clearTimeout(searchRef.current);
		searchRef.current = window.setTimeout(() => {
			fetchStores(); // Trigger fetch after delay
		}, 500);
	};

	const clearFilters = () => {
		setQ('');
		setCategory(null);
		setSort('newest');
		setPage(1);
		setShowMobileFilters(false);
		// fetchStores will trigger via useEffect dependencies
	};

	return (
		<div className="min-h-screen bg-[#F8F9FA]">
			{/* --- Top Bar --- */}
			<div className="bg-white border-b sticky top-0 z-30 shadow-sm">
				<div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
					<div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
						<div className="flex items-center justify-between">
							<h1 className="text-2xl font-bold text-[#051449]">
								Student Stores
							</h1>
							<button
								onClick={() => setShowMobileFilters(true)}
								className="md:hidden p-2 text-gray-600 hover:bg-gray-100 rounded-lg"
							>
								<Filter className="w-5 h-5" />
							</button>
						</div>

						<div className="flex flex-1 md:max-w-xl gap-2">
							<div className="relative flex-1">
								<Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 w-4 h-4" />
								<input
									type="text"
									placeholder="Search stores, universities..."
									value={q}
									onChange={(e) =>
										handleSearchChange(e.target.value)
									}
									className="w-full pl-10 pr-4 py-2.5 bg-gray-50 border border-gray-200 rounded-xl text-sm focus:ring-2 focus:ring-[#051449]/20 focus:border-[#051449] outline-none transition-all"
								/>
							</div>
							<select
								value={sort}
								onChange={(e) => {
									setSort(e.target.value);
									setPage(1);
								}}
								className="hidden md:block px-4 py-2.5 bg-white border border-gray-200 rounded-xl text-sm font-medium text-gray-700 cursor-pointer focus:ring-2 focus:ring-[#051449]/20 outline-none"
							>
								<option value="newest">Newest</option>
								<option value="oldest">Oldest</option>
								<option value="alpha">A-Z</option>
							</select>
						</div>
					</div>
				</div>
			</div>

			<div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
				<div className="flex gap-8">
					{/* --- Sidebar (Desktop) --- */}
					<aside className="hidden md:block w-64 flex-shrink-0 space-y-6">
						<div>
							<h3 className="text-sm font-bold text-[#051449] uppercase tracking-wider mb-4">
								Categories
							</h3>
							<div className="space-y-1">
								<button
									onClick={() => {
										setCategory(null);
										setPage(1);
									}}
									className={`w-full text-left px-3 py-2 rounded-lg text-sm transition-colors ${
										!category
											? 'bg-[#051449] text-white font-medium'
											: 'text-gray-600 hover:bg-gray-100'
									}`}
								>
									All Stores
								</button>
								{BUSINESS_TYPES.map((c) => (
									<button
										key={c}
										onClick={() => {
											setCategory(
												c === category ? null : c,
											);
											setPage(1);
										}}
										className={`w-full text-left px-3 py-2 rounded-lg text-sm transition-colors ${
											c === category
												? 'bg-[#051449] text-white font-medium'
												: 'text-gray-600 hover:bg-gray-100'
										}`}
									>
										{c}
									</button>
								))}
							</div>
						</div>
						<button
							onClick={clearFilters}
							className="w-full py-2 text-sm text-gray-500 hover:text-[#051449] border border-gray-200 hover:border-[#051449] rounded-lg transition-colors"
						>
							Reset Filters
						</button>
					</aside>

					{/* --- Main Content --- */}
					<main className="flex-1">
						{loading ? (
							<div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
								{Array.from({ length: 9 }).map((_, i) => (
									<div
										key={i}
										className="bg-white rounded-2xl h-64 border border-gray-100 p-6 animate-pulse"
									>
										<div className="h-16 w-16 bg-gray-200 rounded-full mb-4" />
										<div className="h-4 bg-gray-200 rounded w-3/4 mb-3" />
										<div className="h-3 bg-gray-200 rounded w-1/2 mb-6" />
										<div className="h-8 bg-gray-200 rounded w-full" />
									</div>
								))}
							</div>
						) : stores.length === 0 ? (
							<div className="flex flex-col items-center justify-center py-20 bg-white rounded-2xl border border-dashed border-gray-200">
								<div className="w-16 h-16 bg-gray-50 rounded-full flex items-center justify-center mb-4">
									<StoreIcon className="w-8 h-8 text-gray-400" />
								</div>
								<h3 className="text-lg font-bold text-gray-900">
									No stores found
								</h3>
								<p className="text-gray-500 mt-2">
									Try adjusting your search or filters
								</p>
								<button
									onClick={clearFilters}
									className="mt-6 px-6 py-2 bg-[#051449] text-white rounded-lg hover:bg-blue-900 transition-colors"
								>
									Clear Filters
								</button>
							</div>
						) : (
							<>
								<div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
									{stores.map((store) => (
										<StoreCard
											key={store._id}
											store={store}
										/>
									))}
								</div>

								{/* Pagination */}
								<div className="mt-12 flex items-center justify-center gap-4">
									<button
										onClick={() =>
											setPage((p) => Math.max(1, p - 1))
										}
										disabled={page <= 1}
										className="p-2 rounded-full border hover:bg-gray-50 disabled:opacity-30 disabled:hover:bg-transparent transition-colors"
									>
										<ChevronLeft className="w-5 h-5" />
									</button>
									<span className="text-sm font-medium text-gray-600">
										Page {page} of{' '}
										{Math.ceil(total / DEFAULT_LIMIT)}
									</span>
									<button
										onClick={() => setPage((p) => p + 1)}
										disabled={
											page >=
											Math.ceil(total / DEFAULT_LIMIT)
										}
										className="p-2 rounded-full border hover:bg-gray-50 disabled:opacity-30 disabled:hover:bg-transparent transition-colors"
									>
										<ChevronRight className="w-5 h-5" />
									</button>
								</div>
							</>
						)}
					</main>
				</div>
			</div>

			{/* --- Mobile Drawer --- */}
			{showMobileFilters && (
				<div className="fixed inset-0 z-50 flex md:hidden">
					<div
						className="fixed inset-0 bg-black/50 backdrop-blur-sm"
						onClick={() => setShowMobileFilters(false)}
					/>
					<div className="relative w-4/5 max-w-xs bg-white h-full shadow-2xl p-6 flex flex-col animate-in slide-in-from-left">
						<div className="flex items-center justify-between mb-6">
							<h2 className="text-lg font-bold text-[#051449]">
								Filters
							</h2>
							<button
								onClick={() => setShowMobileFilters(false)}
								className="p-1 hover:bg-gray-100 rounded-full"
							>
								<X className="w-6 h-6 text-gray-500" />
							</button>
						</div>

						<div className="flex-1 overflow-y-auto space-y-6">
							<div>
								<h3 className="text-xs font-bold text-gray-400 uppercase tracking-wider mb-3">
									Sort By
								</h3>
								<select
									value={sort}
									onChange={(e) => {
										setSort(e.target.value);
										setPage(1);
									}}
									className="w-full p-2 bg-gray-50 border rounded-lg text-sm"
								>
									<option value="newest">Newest</option>
									<option value="oldest">Oldest</option>
									<option value="alpha">A-Z</option>
								</select>
							</div>
							<div>
								<h3 className="text-xs font-bold text-gray-400 uppercase tracking-wider mb-3">
									Categories
								</h3>
								<div className="space-y-2">
									<button
										onClick={() => {
											setCategory(null);
											setShowMobileFilters(false);
										}}
										className={`block w-full text-left px-3 py-2 rounded-lg text-sm ${
											!category
												? 'bg-blue-50 text-blue-700'
												: 'text-gray-600'
										}`}
									>
										All Stores
									</button>
									{BUSINESS_TYPES.map((c) => (
										<button
											key={c}
											onClick={() => {
												setCategory(c);
												setShowMobileFilters(false);
											}}
											className={`block w-full text-left px-3 py-2 rounded-lg text-sm ${
												c === category
													? 'bg-blue-50 text-blue-700'
													: 'text-gray-600'
											}`}
										>
											{c}
										</button>
									))}
								</div>
							</div>
						</div>
					</div>
				</div>
			)}
		</div>
	);
}

// --- Store Card Component ---

function StoreCard({ store }: { store: Store }) {
	return (
		<Link
			href={`/stores/${store.slug}`}
			className="group flex flex-col bg-white rounded-2xl border border-gray-100 shadow-sm hover:shadow-lg hover:border-gray-200 transition-all duration-300 overflow-hidden h-full"
		>
			{/* Banner Area (Gradient Fallback) */}
			<div className="h-24 bg-gradient-to-r from-[#051449] to-blue-900 relative">
				{/* Logo overlapping */}
				<div className="absolute -bottom-8 left-6">
					<div className="h-16 w-16 rounded-xl border-4 border-white bg-white shadow-sm overflow-hidden flex items-center justify-center">
						{store.logoUrl ? (
							<img
								src={store.logoUrl}
								alt={store.name}
								className="w-full h-full object-cover"
							/>
						) : (
							<div className="text-xl font-bold text-gray-300 bg-gray-50 w-full h-full flex items-center justify-center">
								{store.name[0].toUpperCase()}
							</div>
						)}
					</div>
				</div>
			</div>

			<div className="pt-10 p-6 flex-1 flex flex-col">
				<div className="flex justify-between items-start mb-2">
					<div>
						<h3 className="text-lg font-bold text-gray-900 line-clamp-1 group-hover:text-blue-700 transition-colors flex items-center gap-1">
							{store.name}
							{store.owner?.isAlumni && (
								<CheckCircle2 className="w-4 h-4 text-blue-600 fill-blue-100" />
							)}
						</h3>
						<div className="flex items-center text-xs text-gray-500 mt-1">
							<MapPin className="w-3 h-3 mr-1" />
							<span className="truncate max-w-[150px]">
								{store.university || 'Campus Store'}
							</span>
						</div>
					</div>
				</div>

				<p className="text-sm text-gray-600 line-clamp-2 mb-4 flex-1">
					{store.description ||
						'Visit our store to see our amazing products for students.'}
				</p>

				<div className="mt-auto pt-4 border-t border-gray-50 flex items-center justify-between">
					{store.metadata?.category && (
						<span className="text-[10px] uppercase font-semibold tracking-wider text-blue-600 bg-blue-50 px-2 py-1 rounded-md">
							{store.metadata.category}
						</span>
					)}
					<span className="text-xs font-medium text-gray-400 flex items-center group-hover:text-[#051449] transition-colors">
						Visit Store{' '}
						<ExternalLink className="w-3 h-3 ml-1" />
					</span>
				</div>
			</div>
		</Link>
	);
}
